import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import type { MealPlanDto, MealSlot, PlanRecipeDto } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button, buttonClass } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { Field } from '../../components/Field';
import { FlameIcon } from '../../components/icons';
import { Section } from '../../components/Section';
import { Skeleton } from '../../components/Skeleton';
import { SelectField } from '../../components/SelectField';
import { MarkCookedDialog } from '../../components/plans/MarkCookedDialog';
import { PlanAttendeesEditor } from '../../components/plans/PlanAttendeesEditor';
import { AttendeeChips, PlanKindBadge, PlanWarnings } from '../../components/plans/PlanPeople';
import { PlanColorDot } from '../../components/plans/PlanColorDot';
import { RecipePicker } from '../../components/plans/RecipePicker';
import { RemindersSection } from '../../components/plans/RemindersSection';
import { SuggestionsPanel } from '../../components/plans/SuggestionsPanel';
import { TimelineView } from '../../components/plans/TimelineView';
import { api, queryKeys } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import { formatDayLong, zonedDateTimeIso } from '../../lib/planDates';
import { profileKeys, sortPlaces } from '../../lib/profiles';
import { recipeKeys } from '../recipes/queries';
import {
  cookedToast,
  defaultScale,
  DINE_OUT_NOTE_MAX,
  headerOf,
  headerPatch,
  householdServings,
  isLeftovers,
  isValidHeader,
  LEFTOVERS_BADGE_CLASS,
  LEFTOVERS_LABEL,
  leftoversOffer,
  leftoversOfferLabel,
  leftoversWindow,
  nextColorIndex,
  planKindOptions,
  rescaleForServings,
  sameHeader,
  scaleLabel,
  SLOT_LABEL,
  SLOT_OPTIONS,
  STATUS_LABEL,
  type PlanHeaderDraft,
} from '../../lib/plans';

/** Quiet time after the last header edit before it auto-saves. */
const AUTOSAVE_MS = 600;

export function PlanDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { auth } = useAuth();
  const fallbackServings = householdServings(auth.household);

  const [addOpen, setAddOpen] = useState(false);
  const [cookedOpen, setCookedOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busyRecipeId, setBusyRecipeId] = useState<string | null>(null);
  const [draft, setDraft] = useState<PlanHeaderDraft | null>(null);
  /** A half-typed `<input type="time">` reports '' — never auto-save that as "no serve time". */
  const [serveAtIncomplete, setServeAtIncomplete] = useState(false);
  /** The saved header from before the current run of edits, for "Undo". */
  const [undoTo, setUndoTo] = useState<PlanHeaderDraft | null>(null);
  /** The server copy the draft was last seeded from. */
  const seededRef = useRef<PlanHeaderDraft | null>(null);

  const detailKey = queryKeys.plans.detail(id);
  const plan = useQuery({
    queryKey: detailKey,
    queryFn: () => api.plans.get(id),
    enabled: id !== '',
  });

  // Seed the editable header once the plan lands, and whenever the server copy
  // changes — unless the person has edited past the copy it was seeded from
  // (typing on while an auto-save is in flight must not be overwritten).
  useEffect(() => {
    const p = plan.data;
    if (!p) return;
    const next = headerOf(p);
    const seeded = seededRef.current;
    setDraft((d) => (d === null || seeded === null || sameHeader(d, seeded) ? next : d));
    seededRef.current = next;
  }, [plan.data]);

  // A different plan in the same mounted page: its Undo starts afresh.
  useEffect(() => {
    setUndoTo(null);
  }, [id]);

  const editDraft = (patch: Partial<PlanHeaderDraft>) => {
    if (plan.data && undoTo === null) setUndoTo(headerOf(plan.data));
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };

  const invalidate = async () => {
    await qc.invalidateQueries({ queryKey: queryKeys.plans.all });
  };

  /** Optimistic mutations write straight into the cached plan; every one
   *  reconciles with the server's own copy on success (via `setQueryData`,
   *  not just an invalidate, so there is no flash back to the stale value)
   *  and rolls back to the snapshot on failure — same shape as the grocery
   *  list's check-off (`apps/web/src/pages/groceries/GroceryListDetailPage.tsx`). */
  const patchPlan = (updater: (p: MealPlanDto) => MealPlanDto) => {
    qc.setQueryData<MealPlanDto>(detailKey, (old) => (old ? updater(old) : old));
  };
  async function beginOptimistic() {
    await qc.cancelQueries({ queryKey: detailKey });
    return { previous: qc.getQueryData<MealPlanDto>(detailKey) };
  }
  function rollback(ctx: { previous: MealPlanDto | undefined } | undefined) {
    if (ctx?.previous) qc.setQueryData(detailKey, ctx.previous);
  }

  const places = useQuery({
    queryKey: profileKeys.places,
    queryFn: () => api.households.mealPlaces.list(),
  });
  const placeList = sortPlaces(places.data?.places ?? []);
  const homePlace = placeList.find((pl) => pl.kind === 'home');

  type HeaderSave = { next: PlanHeaderDraft; base: PlanHeaderDraft; leftovers: boolean };
  const save = useMutation({
    mutationFn: ({ next, base, leftovers }: HeaderSave) =>
      api.plans.update(id, headerPatch(base, next, leftovers)),
    onMutate: async ({ next, base, leftovers }) => {
      const ctx = await beginOptimistic();
      const extra = headerPatch(base, next, leftovers);
      // Mirrors the API: a servings change rescales every recipe (PLAN §9 #17).
      patchPlan((p) => ({
        ...p,
        ...(extra.kind ? { kind: extra.kind } : {}),
        ...(extra.placeId !== undefined
          ? {
              placeId: extra.placeId,
              placeName: placeList.find((pl) => pl.id === extra.placeId)?.name ?? null,
            }
          : {}),
        ...(extra.dineOutNote !== undefined ? { dineOutNote: extra.dineOutNote } : {}),
        date: next.date,
        slot: next.slot,
        serveAt: next.serveAt ? next.serveAt : null,
        servings: next.servings,
        recipes:
          next.servings === p.servings
            ? p.recipes
            : p.recipes.map((r) => ({
                ...r,
                scale: rescaleForServings(r.scale, r.servings, p.servings, next.servings),
              })),
      }));
      return ctx;
    },
    onError: (_err, _vars, ctx) => rollback(ctx),
    onSuccess: (updated) => {
      if (updated) qc.setQueryData(detailKey, updated);
      // Every timeline for this plan (the editor's preview and cooking mode's).
      void qc.invalidateQueries({ queryKey: queryKeys.plans.timelinesFor(id) });
      void invalidate();
    },
  });

  // Auto-save: once the header has been quiet for a moment and is complete,
  // send it. Leaving the page inside that window still saves (flush on unmount).
  const saved = plan.data ? headerOf(plan.data) : null;
  const dirty = draft !== null && saved !== null && !sameHeader(draft, saved);
  const canSave = dirty && draft !== null && isValidHeader(draft) && !serveAtIncomplete;
  const { mutate: saveHeader, isPending: saving } = save;
  const flushRef = useRef<(() => void) | null>(null);
  const savedPlan = plan.data;
  useEffect(() => {
    if (!draft || !canSave || saving || !savedPlan) {
      flushRef.current = null;
      return;
    }
    const vars = { next: draft, base: headerOf(savedPlan), leftovers: isLeftovers(savedPlan) };
    const fire = () => {
      flushRef.current = null;
      saveHeader(vars);
    };
    flushRef.current = fire;
    const timer = setTimeout(fire, AUTOSAVE_MS);
    return () => clearTimeout(timer);
  }, [draft, canSave, saving, saveHeader, savedPlan]);
  useEffect(() => {
    const pending = flushRef;
    return () => pending.current?.();
  }, []);
  const addRecipe = useMutation({
    mutationFn: (recipe: { id: string; title: string; servings: number | null }) =>
      api.plans.addRecipe(id, { recipeId: recipe.id }),
    onMutate: async (recipe) => {
      const ctx = await beginOptimistic();
      patchPlan((p) => {
        // Already added — directly, or as this household's fork of a shared recipe.
        if (p.recipes.some((r) => r.recipeId === recipe.id || r.forkedFromRecipeId === recipe.id))
          return p;
        // A shared recipe is forked server-side; this placeholder carries the
        // source id only until the server's copy (with the fork's id) replaces it.
        const optimistic: PlanRecipeDto = {
          recipeId: recipe.id,
          title: recipe.title,
          slug: '',
          photoMediaId: null,
          servings: recipe.servings,
          prepMin: null,
          cookMin: null,
          scale: defaultScale(p.servings, recipe.servings),
          colorIndex: nextColorIndex(p.recipes.map((r) => r.colorIndex)),
        };
        return { ...p, recipes: [...p.recipes, optimistic] };
      });
      return ctx;
    },
    onError: (_err, _vars, ctx) => rollback(ctx),
    onSettled: () => setBusyRecipeId(null),
    onSuccess: (updated) => {
      if (updated) qc.setQueryData(detailKey, updated);
      setAddOpen(false);
      void invalidate();
      // Adding a shared recipe may have created a fork in this household's library.
      void qc.invalidateQueries({ queryKey: recipeKeys.all });
    },
  });
  const rescale = useMutation({
    mutationFn: ({ recipeId, scale }: { recipeId: string; scale: number }) =>
      api.plans.updateRecipe(id, recipeId, { scale }),
    onMutate: async ({ recipeId, scale }) => {
      const ctx = await beginOptimistic();
      patchPlan((p) => ({
        ...p,
        recipes: p.recipes.map((r) => (r.recipeId === recipeId ? { ...r, scale } : r)),
      }));
      return ctx;
    },
    onError: (_err, _vars, ctx) => rollback(ctx),
    onSuccess: (updated) => {
      if (updated) qc.setQueryData(detailKey, updated);
      void invalidate();
    },
  });
  const removeRecipe = useMutation({
    mutationFn: (recipeId: string) => api.plans.removeRecipe(id, recipeId),
    onMutate: async (recipeId) => {
      const ctx = await beginOptimistic();
      patchPlan((p) => ({ ...p, recipes: p.recipes.filter((r) => r.recipeId !== recipeId) }));
      return ctx;
    },
    onError: (_err, _vars, ctx) => rollback(ctx),
    onSuccess: (updated) => {
      if (updated) qc.setQueryData(detailKey, updated);
      void invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.plans.remove(id),
    onSuccess: async () => {
      await invalidate();
      qc.removeQueries({ queryKey: detailKey });
      void navigate('/plan', { replace: true, state: { toast: 'Meal removed from the plan.' } });
    },
  });

  const saveLeftovers = useMutation({
    mutationFn: (offer: { date: string; slot: MealSlot; servings: number }) =>
      api.plans.createLeftovers(id, offer),
    onSuccess: () => void invalidate(),
  });

  // Plans in the following week, to tell whether leftovers were already saved.
  const nearbyRange = plan.data ? leftoversWindow(plan.data) : null;
  const nearby = useQuery({
    queryKey: queryKeys.plans.week(nearbyRange?.from ?? '', nearbyRange?.to ?? ''),
    queryFn: () => api.plans.list(nearbyRange!.from, nearbyRange!.to),
    enabled: nearbyRange !== null,
  });

  // Preview the schedule for the serve time being edited, not only the saved
  // one, so "Start cooking at" moves as the person types.
  const previewServeAt =
    draft && !serveAtIncomplete
      ? zonedDateTimeIso(draft.date, draft.serveAt, auth.household?.timezone)
      : undefined;
  const timeline = useQuery({
    queryKey: queryKeys.plans.timeline(id, previewServeAt),
    queryFn: () => api.plans.timeline(id, previewServeAt ? { serveAt: previewServeAt } : {}),
    placeholderData: keepPreviousData,
    enabled:
      id !== '' &&
      (plan.data?.recipes.length ?? 0) > 0 &&
      !(plan.data ? isLeftovers(plan.data) : false),
  });

  if (plan.isPending) {
    return (
      <AppShell>
        <Skeleton lines={4} />
      </AppShell>
    );
  }
  if (plan.isError || !plan.data) {
    return (
      <AppShell title="Meal">
        <Alert tone="error">{messageForError(plan.error)}</Alert>
        <Link to="/plan" className="mt-3 inline-block text-sm text-brand underline">
          Back to the week
        </Link>
      </AppShell>
    );
  }

  const p = plan.data;
  const canUndo = undoTo !== null && draft !== null && !sameHeader(undoTo, draft);
  const saveStatus = save.isPending
    ? 'Saving…'
    : draft && (!isValidHeader(draft) || serveAtIncomplete)
      ? 'Finish the date, time, servings and note to save.'
      : dirty
        ? 'Saving…'
        : 'All changes saved';
  // Shared recipes already planned as this household's fork count as planned too.
  const plannedRecipeIds = p.recipes.flatMap((r) =>
    r.forkedFromRecipeId ? [r.recipeId, r.forkedFromRecipeId] : [r.recipeId],
  );
  const leftovers = isLeftovers(p);
  const offer = nearby.data ? leftoversOffer(p, nearby.data.plans) : null;

  return (
    <AppShell width="wide">
      <div className="flex flex-col gap-5">
        <div className="on-teal relative overflow-hidden rounded-3xl bg-brand p-5 text-white shadow-[var(--shadow-lift)] sm:p-6">
          <span
            aria-hidden
            className="pointer-events-none absolute -top-12 -right-10 size-44 rounded-full bg-accent/25"
          />
          <Link
            to="/plan"
            className="relative inline-flex min-h-9 items-center text-sm font-medium text-white/85 underline-offset-2 hover:text-white hover:underline"
          >
            ← Back to the week
          </Link>
          <h1 className="relative mt-1 text-2xl text-white sm:text-3xl">
            {SLOT_LABEL[p.slot]} · {formatDayLong(p.date)}
          </h1>
          <div className="relative mt-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-accent px-3 py-1 text-sm font-semibold text-ink">
              {STATUS_LABEL[p.status]}
            </span>
            {leftovers ? (
              <span
                className={`rounded-full px-3 py-1 text-sm font-semibold ${LEFTOVERS_BADGE_CLASS}`}
              >
                {LEFTOVERS_LABEL}
              </span>
            ) : null}
            <PlanKindBadge plan={p} />
            {p.serveAt ? (
              <span className="rounded-full bg-white/15 px-3 py-1 text-sm text-white">
                Serve at {p.serveAt}
              </span>
            ) : null}
            <span className="rounded-full bg-white/15 px-3 py-1 text-sm text-white">
              Serves {p.servings}
            </span>
            {p.recipes.map((r) => (
              <span
                key={r.recipeId}
                className="inline-flex items-center gap-1.5 rounded-full bg-cream px-3 py-1 text-sm font-medium text-ink"
              >
                <PlanColorDot colorIndex={r.colorIndex} label={r.title} size={10} />
                <span className="max-w-48 truncate">{r.title}</span>
              </span>
            ))}
          </div>
          <div className="relative mt-3">
            <AttendeeChips plan={p} />
          </div>
          {p.kind === 'dine_out' && p.dineOutNote ? (
            <p className="relative mt-3 text-sm text-white/90">{p.dineOutNote}</p>
          ) : null}
          {leftovers ? (
            <p className="relative mt-3 text-sm text-white/90">
              {p.leftoversOf ? (
                <>
                  Leftovers from{' '}
                  <Link
                    to={`/plan/${p.leftoversOf.planId}`}
                    className="font-semibold text-white underline underline-offset-2"
                  >
                    {p.leftoversOf.title}
                  </Link>{' '}
                  ({SLOT_LABEL[p.leftoversOf.slot].toLowerCase()},{' '}
                  {formatDayLong(p.leftoversOf.date)})
                </>
              ) : (
                'Leftovers from a meal that has since been removed'
              )}
            </p>
          ) : null}
        </div>

        <PlanWarnings plan={p} />

        <div
          className={
            leftovers
              ? 'flex flex-col gap-5'
              : 'grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-start lg:gap-6'
          }
        >
          <div className="flex flex-col gap-5">
            <Section title="When and how much" accent="teal">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="flex flex-col gap-1">
                  <label htmlFor="plan-date" className="text-sm font-medium text-ink">
                    Date
                  </label>
                  <input
                    id="plan-date"
                    type="date"
                    value={draft?.date ?? ''}
                    onChange={(e) => editDraft({ date: e.target.value })}
                    className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                  />
                </div>
                <SelectField
                  id="plan-slot"
                  label="Meal"
                  value={draft?.slot ?? 'dinner'}
                  onChange={(e) => editDraft({ slot: e.target.value as MealSlot })}
                  options={SLOT_OPTIONS}
                />
                <div className="flex flex-col gap-1">
                  <label htmlFor="plan-serve-at" className="text-sm font-medium text-ink">
                    Serve at
                  </label>
                  <input
                    id="plan-serve-at"
                    type="time"
                    value={draft?.serveAt ?? ''}
                    onChange={(e) => {
                      setServeAtIncomplete(e.target.validity.badInput);
                      editDraft({ serveAt: e.target.value });
                    }}
                    className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label htmlFor="plan-servings" className="text-sm font-medium text-ink">
                    Servings
                  </label>
                  <input
                    id="plan-servings"
                    type="number"
                    min={1}
                    max={50}
                    value={draft ? draft.servings || '' : fallbackServings}
                    onChange={(e) => {
                      // An emptied field is "still typing" (0 → not saved), never 1.
                      const n = Number(e.target.value);
                      editDraft({
                        servings:
                          e.target.value !== '' && Number.isFinite(n) && n > 0
                            ? Math.min(50, Math.round(n))
                            : 0,
                      });
                    }}
                    className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                  />
                </div>
                {leftovers || p.kind === 'leftovers' || !draft ? null : (
                  <>
                    <SelectField
                      id="plan-kind"
                      label="Type"
                      value={draft.kind}
                      onChange={(e) =>
                        editDraft({ kind: e.target.value as PlanHeaderDraft['kind'] })
                      }
                      options={planKindOptions(p.kind, p.recipes.length)}
                    />
                    {draft.kind === 'dine_out' ? (
                      <Field
                        id="plan-dine-out-note"
                        label="Where you’re eating (optional)"
                        placeholder="Pizza night at Luigi’s"
                        maxLength={DINE_OUT_NOTE_MAX}
                        value={draft.dineOutNote}
                        onChange={(e) => editDraft({ dineOutNote: e.target.value })}
                      />
                    ) : (
                      <SelectField
                        id="plan-place"
                        label="Eaten at"
                        value={draft.placeId || homePlace?.id || ''}
                        onChange={(e) => {
                          const v = e.target.value;
                          // Home is stored as "no place" unless the plan already names it.
                          editDraft({
                            placeId: v === homePlace?.id && p.placeId !== v ? '' : v,
                          });
                        }}
                        options={
                          placeList.length
                            ? placeList.map((pl) => ({ value: pl.id, label: pl.name }))
                            : [{ value: '', label: 'Home' }]
                        }
                      />
                    )}
                  </>
                )}
              </div>
              {!leftovers && p.recipes.length > 0 && p.kind !== 'dine_out' ? (
                <p className="text-xs text-muted">
                  To mark this as dining out, take its recipes off first.
                </p>
              ) : null}
              {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}
              <div className="flex min-h-11 flex-wrap items-center gap-3">
                <p role="status" className="text-sm text-muted">
                  {saveStatus}
                </p>
                {canUndo ? (
                  <Button
                    variant="secondary"
                    block={false}
                    onClick={() => {
                      setServeAtIncomplete(false);
                      setDraft(undoTo);
                      setUndoTo(null);
                    }}
                  >
                    Undo
                  </Button>
                ) : null}
              </div>
            </Section>

            {leftovers ? null : (
              <Section
                title="Recipes"
                accent="orange"
                description="Scale each recipe or take it off this meal."
              >
                {removeRecipe.isError ? (
                  <Alert tone="error">{messageForError(removeRecipe.error)}</Alert>
                ) : null}
                {rescale.isError ? (
                  <Alert tone="error">{messageForError(rescale.error)}</Alert>
                ) : null}
                {p.recipes.length === 0 ? (
                  <p className="text-sm text-muted">Nothing on this meal yet.</p>
                ) : (
                  <ul className="flex flex-col divide-y divide-line">
                    {p.recipes.map((recipe) => (
                      <li key={recipe.recipeId} className="flex flex-wrap items-center gap-2 py-2">
                        <PlanColorDot
                          colorIndex={recipe.colorIndex}
                          label={recipe.title}
                          size={12}
                        />
                        <Link
                          to={`/recipes/${recipe.recipeId}`}
                          className="min-w-0 flex-1 truncate text-base text-ink underline-offset-2 hover:underline"
                        >
                          {recipe.title}
                        </Link>
                        <span className="text-xs text-muted">{scaleLabel(recipe.scale)}</span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            aria-label={`Scale ${recipe.title} down`}
                            disabled={rescale.isPending || recipe.scale <= 0.25}
                            onClick={() =>
                              rescale.mutate({
                                recipeId: recipe.recipeId,
                                scale: Math.max(
                                  0.25,
                                  Math.round((recipe.scale - 0.25) * 100) / 100,
                                ),
                              })
                            }
                            className="size-11 rounded-xl border border-line text-lg text-brand transition-colors hover:bg-brand-soft disabled:opacity-50"
                          >
                            <span aria-hidden>−</span>
                          </button>
                          <button
                            type="button"
                            aria-label={`Scale ${recipe.title} up`}
                            disabled={rescale.isPending || recipe.scale >= 20}
                            onClick={() =>
                              rescale.mutate({
                                recipeId: recipe.recipeId,
                                scale: Math.min(20, Math.round((recipe.scale + 0.25) * 100) / 100),
                              })
                            }
                            className="size-11 rounded-xl border border-line text-lg text-brand transition-colors hover:bg-brand-soft disabled:opacity-50"
                          >
                            <span aria-hidden>+</span>
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove ${recipe.title} from this meal`}
                            disabled={removeRecipe.isPending}
                            onClick={() => removeRecipe.mutate(recipe.recipeId)}
                            className={buttonClass({ variant: 'danger', size: 'sm', block: false })}
                          >
                            Remove
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {addRecipe.isError ? (
                  <Alert tone="error">{messageForError(addRecipe.error)}</Alert>
                ) : null}
                <Button variant="secondary" onClick={() => setAddOpen(true)}>
                  Add a recipe
                </Button>
              </Section>
            )}

            {offer ? (
              <Section
                title="Leftovers"
                accent="orange"
                description="This meal makes more than it feeds. Put the rest on the calendar."
              >
                {saveLeftovers.isError ? (
                  <Alert tone="error">{messageForError(saveLeftovers.error)}</Alert>
                ) : null}
                {saveLeftovers.isSuccess ? (
                  <p className="text-sm text-muted">Leftovers saved to the calendar.</p>
                ) : (
                  <div>
                    <Button
                      variant="secondary"
                      block={false}
                      disabled={saveLeftovers.isPending}
                      onClick={() => saveLeftovers.mutate(offer)}
                    >
                      {leftoversOfferLabel(offer, formatDayLong)}
                    </Button>
                  </div>
                )}
              </Section>
            ) : null}

            <PlanAttendeesEditor plan={p} />

            <RemindersSection plan={p} />

            <Section title="Actions" accent="neutral">
              {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
              <div className="flex flex-col gap-2 sm:flex-row">
                <Button onClick={() => setCookedOpen(true)} disabled={p.status === 'cooked'}>
                  {p.status === 'cooked'
                    ? leftovers
                      ? 'Already eaten'
                      : 'Already cooked'
                    : leftovers
                      ? 'Mark eaten'
                      : 'Mark cooked'}
                </Button>
                <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                  Remove this meal
                </Button>
              </div>
            </Section>
          </div>

          {leftovers ? null : (
            <div className="flex flex-col gap-5">
              <SuggestionsPanel
                date={p.date}
                slot={p.slot}
                excludeIds={plannedRecipeIds}
                busyRecipeId={busyRecipeId}
                onAdd={(recipe) => {
                  setBusyRecipeId(recipe.recipeId);
                  addRecipe.mutate({ id: recipe.recipeId, title: recipe.title, servings: null });
                }}
              />

              <Section
                title="Cooking timeline"
                accent="teal"
                description="Every step from both recipes, in one order."
              >
                {p.recipes.length === 0 ? (
                  <p className="text-sm text-muted">Add a recipe and the timeline appears here.</p>
                ) : timeline.isPending ? (
                  <Skeleton lines={5} label="Working out the order" />
                ) : timeline.isError ? (
                  <Alert tone="error">{messageForError(timeline.error)}</Alert>
                ) : timeline.data ? (
                  <>
                    <TimelineView timeline={timeline.data} />
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/plan/${p.id}/cook`} className={buttonClass({ block: false })}>
                        <FlameIcon className="size-4" /> Start cooking
                      </Link>
                      <Link
                        to={`/plan/${p.id}/print`}
                        className={buttonClass({ variant: 'secondary', block: false })}
                      >
                        Print
                      </Link>
                    </div>
                  </>
                ) : null}
              </Section>
            </div>
          )}
        </div>
      </div>

      {addOpen ? (
        <Dialog open onClose={() => setAddOpen(false)} title="Add a recipe to this meal">
          <RecipePicker
            autoFocus
            busy={addRecipe.isPending}
            excludeIds={plannedRecipeIds}
            onPick={(recipe) => {
              setBusyRecipeId(recipe.id);
              addRecipe.mutate(recipe);
            }}
          />
          <Button variant="secondary" onClick={() => setAddOpen(false)}>
            Cancel
          </Button>
        </Dialog>
      ) : null}

      {cookedOpen ? (
        <MarkCookedDialog
          open
          onClose={() => setCookedOpen(false)}
          planId={id}
          plan={p}
          onCooked={(outcome) => void navigate('/plan', { state: { toast: cookedToast(outcome) } })}
        />
      ) : null}

      {confirmDelete ? (
        <Dialog open onClose={() => setConfirmDelete(false)} title="Remove this meal?">
          <p className="text-sm text-ink">
            The meal comes off the calendar. The recipes themselves are not touched.
          </p>
          <div className="flex gap-2">
            <Button
              variant="danger"
              disabled={remove.isPending}
              onClick={() => {
                setConfirmDelete(false);
                remove.mutate();
              }}
            >
              Remove it
            </Button>
            <Button variant="secondary" onClick={() => setConfirmDelete(false)}>
              Keep it
            </Button>
          </div>
        </Dialog>
      ) : null}
    </AppShell>
  );
}
